/**
 * Audit — the Diner Bookings Information Audit (DN_001 audit proc, cached in
 * cache.DinerBookingsAudit and served via ?report=bookings). Two views:
 *
 *   Detail  — the full per-item audit grid (expected vs ordered, variance,
 *             over/short, allergens, notes) — the raw report.
 *   Summary — a clean one-row-per-booking rollup: expected / ordered / variance
 *             totals, allergen flag and an OK / Over / Short status. This is the
 *             summarised view the flat report doesn't give you.
 *
 * Both are searchable and print to PDF. No new API — reuses the audit endpoint.
 */
import { useEffect, useMemo, useState } from "react";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { variantLabel, variantParam, type CountVariant, type DedupInfo } from "./bookingsVariant";

interface AuditRow {
  id: number;
  Day: string | null; JourneyDate: string | null; Time: string | null; Journey: string | null;
  Carriage: string | null; Seats: string | null; Booking: number | null; AccName: string | null;
  TelephoneNo: string | null; Item: string | null; Price: number | null; Expected: number | null;
  Ordered: number | null; Variance: number | null; OverOrShort: string | null;
  AllergensTicked: string | null; Notes: string | null;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
function prettyDate(v: string | null): string {
  if (!v) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(v);
  return m ? `${m[3]} ${MONTHS[Number(m[2]) - 1]} ${m[1]}` : v;
}
function money(v: number | null): string {
  return v == null ? "" : `£${Number(v).toFixed(2)}`;
}

// One clean line per booking.
interface AuditSummaryRow {
  key: string;
  Day: string | null; JourneyDate: string | null; Time: string | null; Journey: string | null;
  Carriage: string | null; Seats: string | null; Booking: number | null; AccName: string | null;
  Allergens: string; Items: number; Expected: number; Ordered: number; Variance: number;
  Status: "OK" | "Over" | "Short";
}

function rollUp(rows: AuditRow[]): AuditSummaryRow[] {
  const map = new Map<string, AuditSummaryRow>();
  for (const r of rows) {
    const key = `${r.JourneyDate ?? ""}|${r.Time ?? ""}|${r.Booking ?? ""}`;
    let s = map.get(key);
    if (!s) {
      s = {
        key,
        Day: r.Day, JourneyDate: r.JourneyDate, Time: r.Time, Journey: r.Journey,
        Carriage: r.Carriage, Seats: r.Seats, Booking: r.Booking, AccName: r.AccName,
        Allergens: "No", Items: 0, Expected: 0, Ordered: 0, Variance: 0, Status: "OK",
      };
      map.set(key, s);
    }
    s.Items += 1;
    s.Expected += r.Expected ?? 0;
    s.Ordered += r.Ordered ?? 0;
    s.Variance += r.Variance ?? 0;
    if (r.AllergensTicked === "Yes") s.Allergens = "Yes";
  }
  const out = [...map.values()];
  for (const s of out) s.Status = s.Variance > 0 ? "Over" : s.Variance < 0 ? "Short" : "OK";
  out.sort((a, b) => a.key.localeCompare(b.key));
  return out;
}

const DETAIL_COLS: { key: keyof AuditRow; label: string; align?: "right"; fmt?: (v: unknown) => string }[] = [
  { key: "Day", label: "Day" },
  { key: "JourneyDate", label: "Date", fmt: (v) => prettyDate(v as string | null) },
  { key: "Time", label: "Time" },
  { key: "Journey", label: "Journey" },
  { key: "Carriage", label: "Carriage" },
  { key: "Seats", label: "Seats" },
  { key: "Booking", label: "Booking", align: "right" },
  { key: "AccName", label: "Account" },
  { key: "Item", label: "Item" },
  { key: "Price", label: "Price", align: "right", fmt: (v) => money(v as number | null) },
  { key: "Expected", label: "Expected", align: "right" },
  { key: "Ordered", label: "Ordered", align: "right" },
  { key: "Variance", label: "Variance", align: "right" },
  { key: "OverOrShort", label: "Over / Short" },
  { key: "AllergensTicked", label: "Allergens" },
  { key: "Notes", label: "Notes" },
];

const SUMMARY_COLS: { key: keyof AuditSummaryRow; label: string; align?: "right"; fmt?: (v: unknown) => string }[] = [
  { key: "Day", label: "Day" },
  { key: "JourneyDate", label: "Date", fmt: (v) => prettyDate(v as string | null) },
  { key: "Time", label: "Time" },
  { key: "Journey", label: "Journey" },
  { key: "Carriage", label: "Carriage" },
  { key: "Seats", label: "Seats" },
  { key: "Booking", label: "Booking", align: "right" },
  { key: "AccName", label: "Account" },
  { key: "Allergens", label: "Allergens" },
  { key: "Items", label: "Items", align: "right" },
  { key: "Expected", label: "Expected", align: "right" },
  { key: "Ordered", label: "Ordered", align: "right" },
  { key: "Variance", label: "Variance", align: "right" },
  { key: "Status", label: "Status" },
];

export function AuditView(props: {
  from: string;
  to: string;
  reloadNonce: number;
  onLastRefresh?: (v: string | null) => void;
  /** Counting variant (As MERAC reports / One count per booking) — rides on the fetch. */
  variant?: CountVariant;
  onDedup?: (d: DedupInfo | null) => void;
}) {
  const { from, to, reloadNonce, onLastRefresh, onDedup } = props;
  const variant: CountVariant = props.variant ?? "merac";
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<"detail" | "summary">("summary");
  const [search, setSearch] = useState("");
  // Two filters the catering teams actually work from: who needs an allergen
  // brief, and which bookings don't reconcile. They combine with each other and
  // with the search, and they feed the PDF because it prints the filtered rows.
  const [onlyAllergens, setOnlyAllergens] = useState(false);
  const [onlyVariance, setOnlyVariance] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (from > to) { setError("'From' must be on or before 'To'."); setRows([]); return; }
    setLoading(true);
    setError(null);
    fetch(`/api/dashboards/bookings/reports?report=bookings&from=${from}&to=${to}&limit=20000${variantParam(variant)}`, { credentials: "include" })
      .then(async (res) => {
        if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error || `Request failed (${res.status})`); }
        return res.json() as Promise<{ rows: AuditRow[]; last_refresh?: string | null; dedup?: DedupInfo }>;
      })
      .then((data) => { if (cancelled) return; setRows(data.rows ?? []); onLastRefresh?.(data.last_refresh ?? null); onDedup?.(data.dedup ?? null); })
      .catch((err) => { if (cancelled) return; setError(err instanceof Error ? err.message : String(err)); setRows([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [from, to, reloadNonce, onLastRefresh, onDedup, variant]);

  const summary = useMemo(() => rollUp(rows), [rows]);

  const detailFiltered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (onlyAllergens && r.AllergensTicked !== "Yes") return false;
      if (onlyVariance && !(r.Variance != null && r.Variance !== 0)) return false;
      if (!q) return true;
      return [r.AccName, r.Booking, r.Item, r.Journey, r.Carriage]
        .some((v) => v != null && String(v).toLowerCase().includes(q));
    });
  }, [rows, search, onlyAllergens, onlyVariance]);

  const summaryFiltered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return summary.filter((r) => {
      if (onlyAllergens && r.Allergens !== "Yes") return false;
      if (onlyVariance && r.Variance === 0) return false;
      if (!q) return true;
      return [r.AccName, r.Booking, r.Journey, r.Carriage]
        .some((v) => v != null && String(v).toLowerCase().includes(q));
    });
  }, [summary, search, onlyAllergens, onlyVariance]);

  // Counts on the pills, so you can see whether it is worth pressing before you
  // press it. Independent of the pills themselves — always the whole date range.
  const allergenCount = useMemo(() => (
    view === "summary"
      ? summary.filter((r) => r.Allergens === "Yes").length
      : rows.filter((r) => r.AllergensTicked === "Yes").length
  ), [view, summary, rows]);

  const varianceCount = useMemo(() => (
    view === "summary"
      ? summary.filter((r) => r.Variance !== 0).length
      : rows.filter((r) => r.Variance != null && r.Variance !== 0).length
  ), [view, summary, rows]);

  const onPrint = () => {
    if (view === "summary") printAuditSummaryPdf(summaryFiltered, { from, to }, variantLabel(variant));
    else printAuditDetailPdf(detailFiltered, { from, to }, variantLabel(variant));
  };

  const count = view === "summary" ? summaryFiltered.length : detailFiltered.length;

  return (
    <div className="mv">
      <div className="mv-toolbar">
        <div className="bookings-reports" role="tablist" aria-label="Audit view">
          <button type="button" role="tab" aria-selected={view === "summary"} className={"bookings-report-pill" + (view === "summary" ? " active" : "")} onClick={() => setView("summary")}>Summary</button>
          <button type="button" role="tab" aria-selected={view === "detail"} className={"bookings-report-pill" + (view === "detail" ? " active" : "")} onClick={() => setView("detail")}>Detail</button>
        </div>
        <div className="bookings-reports" role="group" aria-label="Audit filters">
          <button
            type="button"
            aria-pressed={onlyAllergens}
            className={"bookings-report-pill" + (onlyAllergens ? " active" : "")}
            title="Show only bookings with an allergen ticked"
            onClick={() => setOnlyAllergens((v) => !v)}
          >
            Allergens{allergenCount > 0 ? ` (${allergenCount})` : ""}
          </button>
          <button
            type="button"
            aria-pressed={onlyVariance}
            className={"bookings-report-pill" + (onlyVariance ? " active" : "")}
            title="Show only bookings that don't reconcile — overs and shorts"
            onClick={() => setOnlyVariance((v) => !v)}
          >
            Variance{varianceCount > 0 ? ` (${varianceCount})` : ""}
          </button>
        </div>
        <div className="mv-toolbar-actions">
          <label className="bookings-search">
            <span className="bookings-search-icon" aria-hidden="true">🔍</span>
            <input type="search" value={search} placeholder="Search name, booking, item…" onChange={(e) => setSearch(e.target.value)} />
            {search && <button type="button" className="bookings-search-clear" aria-label="Clear search" onClick={() => setSearch("")}>×</button>}
          </label>
          <span className="bookings-count">{loading ? "Loading…" : `${count.toLocaleString()} ${view === "summary" ? "bookings" : "rows"}`}</span>
          <button type="button" className="mv-print" onClick={onPrint} disabled={loading || count === 0}>🖨 Generate PDF</button>
        </div>
      </div>

      <div className="bookings-table-wrap">
        {error ? (
          <div className="bookings-empty bookings-empty--error">Couldn’t load audit: {error}</div>
        ) : loading && rows.length === 0 ? (
          <div className="bookings-empty">Loading…</div>
        ) : count === 0 ? (
          <div className="bookings-empty">
            {onlyAllergens || onlyVariance || search
              ? "Nothing matches those filters — clear them to see the whole date range."
              : "No audit rows in this date range."}
          </div>
        ) : view === "summary" ? (
          <table className="bookings-table">
            <thead><tr>{SUMMARY_COLS.map((c) => <th key={c.key} className={c.align ? `ta-${c.align}` : undefined}>{c.label}</th>)}</tr></thead>
            <tbody>
              {summaryFiltered.map((r) => (
                <tr key={r.key} className={r.Status === "Over" ? "aud-over" : r.Status === "Short" ? "aud-short" : undefined}>
                  {SUMMARY_COLS.map((c) => {
                    const raw = r[c.key];
                    const text = c.fmt ? c.fmt(raw) : raw == null ? "" : String(raw);
                    return <td key={c.key} className={c.align ? `ta-${c.align}` : undefined} title={text}>{text}</td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <table className="bookings-table">
            <thead><tr>{DETAIL_COLS.map((c) => <th key={c.key} className={c.align ? `ta-${c.align}` : undefined}>{c.label}</th>)}</tr></thead>
            <tbody>
              {detailFiltered.map((r, i) => (
                <tr key={r.id ?? i}>
                  {DETAIL_COLS.map((c) => {
                    const raw = r[c.key];
                    const text = c.fmt ? c.fmt(raw) : raw == null ? "" : String(raw);
                    return <td key={c.key} className={c.align ? `ta-${c.align}` : undefined} title={text}>{text}</td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// --- PDF builders (landscape, direct download) --------------------------------
type Cell = string;
function bandedPdf(title: string, head: string[], body: Cell[][], range: { from: string; to: string }, colStyles: Record<number, { halign?: "right"; cellWidth?: number }>, fileTag: string, countingLabel?: string) {
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const printedOn = new Date().toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false });
  doc.setFont("helvetica", "bold"); doc.setFontSize(13); doc.setTextColor(11, 61, 46);
  doc.text(title, 8, 12);
  doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(90, 90, 90);
  doc.text(`${range.from} to ${range.to}  ·  ${body.length} row${body.length === 1 ? "" : "s"}${countingLabel ? `  ·  ${countingLabel}` : ""}  ·  printed ${printedOn}`, 8, 17);
  autoTable(doc, {
    head: [head], body, startY: 21, margin: { left: 8, right: 8 },
    styles: { fontSize: 7, cellPadding: 1.2, overflow: "linebreak", valign: "top" },
    headStyles: { fillColor: [31, 77, 63], textColor: 255, fontStyle: "bold" },
    alternateRowStyles: { fillColor: [250, 248, 242] },
    columnStyles: colStyles,
  });
  doc.save(`audit-${fileTag}-${range.from}_to_${range.to}.pdf`);
}

function printAuditSummaryPdf(rows: AuditSummaryRow[], range: { from: string; to: string }, countingLabel?: string) {
  const head = SUMMARY_COLS.map((c) => c.label);
  const body = rows.map((r) => SUMMARY_COLS.map((c) => {
    const raw = r[c.key];
    return c.fmt ? c.fmt(raw) : raw == null ? "" : String(raw);
  }));
  const colStyles: Record<number, { halign?: "right"; cellWidth?: number }> = {};
  SUMMARY_COLS.forEach((c, i) => { if (c.align) colStyles[i] = { halign: "right" }; });
  bandedPdf("Dining & Events — Audit summary (per booking)", head, body, range, colStyles, "summary", countingLabel);
}

function printAuditDetailPdf(rows: AuditRow[], range: { from: string; to: string }, countingLabel?: string) {
  const head = DETAIL_COLS.map((c) => c.label);
  const body = rows.map((r) => DETAIL_COLS.map((c) => {
    const raw = r[c.key];
    return c.fmt ? c.fmt(raw) : raw == null ? "" : String(raw);
  }));
  const colStyles: Record<number, { halign?: "right"; cellWidth?: number }> = { 15: { cellWidth: 45 } };
  DETAIL_COLS.forEach((c, i) => { if (c.align) colStyles[i] = { halign: "right" }; });
  bandedPdf("Dining & Events — Audit detail", head, body, range, colStyles, "detail", countingLabel);
}
